/**
 * Verify Email Page
 * 
 * Handles email verification when user clicks the verification link in their email.
 * Extracts the token from URL, calls backend to verify, then redirects to login.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email address...');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    // Call the backend to verify the email
    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully! You can now login.');
        } else {
          setStatus('error');
          // Handle specific error messages from backend
          const errorDetail = data.detail || '';
          if (typeof errorDetail === 'string') {
            if (errorDetail.includes('already been used')) {
              setMessage('This verification link has already been used. Please login or request a new verification email.');
            } else if (errorDetail.includes('expired')) {
              setMessage('This verification link has expired. Please request a new verification email.');
            } else if (errorDetail.includes('Invalid') || errorDetail.includes('not found') || errorDetail.toLowerCase().includes('token')) {
              setMessage('Invalid verification link. The token may be incorrect or expired. Please request a new verification email.');
            } else {
              setMessage(errorDetail || 'Verification failed. Please try again or contact support.');
            }
          } else {
            setMessage('Verification failed. Please try again or contact support.');
          }
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('An error occurred while verifying your email. Please try again.');
      }
    };

    verifyEmail();
  }, [token]);

  // Countdown timer for success state
  useEffect(() => {
    if (status === 'success') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/login');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, navigate]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">
            <span className="text-white">TITLE</span>
            <span className="text-blue-400 italic">Flow</span>
          </h1>
          <p className="text-slate-400 mt-2">Email Verification</p>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            {/* Status Icon */}
            <div className="mb-6 flex justify-center">
              {status === 'verifying' && (
                <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                </div>
              )}
              {status === 'success' && (
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              )}
              {status === 'error' && (
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
              )}
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {status === 'verifying' && 'Verifying Email'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </h2>

            {/* Message */}
            <p className="text-gray-600 mb-6">
              {message}
            </p>

            {/* Success: Countdown and button */}
            {status === 'success' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Redirecting to login in <span className="font-semibold text-blue-600">{countdown}</span> seconds...
                </p>
                <button
                  onClick={handleGoToLogin}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                  data-testid="verify-email-login-btn"
                >
                  Go to Login Now
                </button>
              </div>
            )}

            {/* Error: Action button */}
            {status === 'error' && (
              <div className="space-y-4">
                <button
                  onClick={handleGoToLogin}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                  data-testid="verify-email-back-btn"
                >
                  Go to Login
                </button>
                <p className="text-sm text-gray-500">
                  If you need a new verification email, please contact your administrator.
                </p>
              </div>
            )}

            {/* Verifying: Loading indicator */}
            {status === 'verifying' && (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <Mail className="w-4 h-4" />
                <span className="text-sm">Please wait while we verify your email...</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm">
            © 2026 PNA Title Services. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
